/**
 * 错误处理器 - 统一错误处理和日志记录
 */

// =============================================
// 自定义错误类型
// =============================================

/**
 * 基础应用错误类
 */
export class AppError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 500,
    public details?: any
  ) {
    super(message);
    this.name = this.constructor.name;
    const captureStackTrace = (Error as ErrorConstructor & {
      captureStackTrace?: (targetObject: object, constructorOpt?: Function) => void;
    }).captureStackTrace;
    captureStackTrace?.(this, this.constructor);
  }
}

/**
 * 存储错误
 */
export class StorageError extends AppError {
  constructor(
    message: string,
    public operation: 'get' | 'set' | 'delete' | 'query' | 'other',
    details?: any
  ) {
    super(message, 'STORAGE_ERROR', 500, details);
  }
}

/**
 * 认证错误
 */
export class AuthError extends AppError {
  constructor(
    message: string,
    public authType: 'login' | 'register' | 'logout' | 'token' | 'permission',
    statusCode: number = 401,
    details?: any
  ) {
    super(message, 'AUTH_ERROR', statusCode, details);
  }
}

/**
 * 网络错误
 */
export class NetworkError extends AppError {
  constructor(
    message: string,
    public operation: string,
    details?: any
  ) {
    super(message, 'NETWORK_ERROR', 0, details); // statusCode 0 表示网络层错误
  }
}

/**
 * 验证错误
 */
export class ValidationError extends AppError {
  constructor(
    message: string,
    public field?: string,
    details?: any
  ) {
    super(message, 'VALIDATION_ERROR', 400, details);
  }
}

/**
 * 业务逻辑错误
 */
export class BusinessError extends AppError {
  constructor(
    message: string,
    details?: any
  ) {
    super(message, 'BUSINESS_ERROR', 400, details);
  }
}

// =============================================
// 错误日志级别
// =============================================

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
  FATAL = 'fatal',
}

// =============================================
// 错误日志条目
// =============================================

export interface ErrorLog {
  id: string;
  timestamp: string;
  level: LogLevel;
  code: string;
  message: string;
  stack?: string;
  context?: {
    operation?: string;
    userId?: string;
    key?: string;
    data?: any;
  };
  details?: any;
  userAgent?: string;
  url?: string;
}

// =============================================
// 错误处理器类
// =============================================

export class ErrorHandler {
  private logs: ErrorLog[] = [];
  private maxLogs: number = 100; // 内存中最多保存的日志数量
  private isDevMode: boolean;

  constructor() {
    this.isDevMode = (() => {
      try {
        return typeof import.meta !== 'undefined' && import.meta.env?.DEV === true;
      } catch {
        return false;
      }
    })();
  }

  /**
   * 处理错误 - 统一入口
   */
  handleError(error: Error | AppError | unknown, context?: any): AppError {
    // 将未知错误转换为 AppError
    let appError: AppError;

    if (error instanceof AppError) {
      appError = error;
    } else if (error instanceof Error) {
      // 标准错误包装为 AppError
      appError = new AppError(
        error.message,
        'UNKNOWN_ERROR',
        500,
        error.stack
      );
    } else {
      // 非错误对象包装为 AppError
      appError = new AppError(
        String(error),
        'UNKNOWN_ERROR',
        500
      );
    }

    // 记录日志
    this.log(appError, context);

    // 返回用户友好的错误信息
    return appError;
  }

  /**
   * 记录错误日志
   */
  private log(error: AppError, context?: any): void {
    const log: ErrorLog = {
      id: this.generateLogId(),
      timestamp: new Date().toISOString(),
      level: this.getLogLevel(error),
      code: error.code,
      message: error.message,
      stack: error.stack,
      context: {
        operation: (error as any).operation,
        userId: context?.userId,
        key: context?.key,
        data: this.isDevMode ? context?.data : undefined, // 生产环境不记录敏感数据
      },
      details: this.isDevMode ? error.details : undefined,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    this.logs.push(log);

    // 限制日志数量
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // 输出到控制台
    this.outputToConsole(log);
  }

  /**
   * 输出到控制台
   */
  private outputToConsole(log: ErrorLog): void {
    const prefix = `[ErrorHandler] [${log.level.toUpperCase()}]`;
    const message = `${prefix} ${log.code}: ${log.message}`;

    switch (log.level) {
      case LogLevel.DEBUG:
        console.debug(message, log.context, log.details);
        break;
      case LogLevel.INFO:
        console.info(message, log.context);
        break;
      case LogLevel.WARN:
        console.warn(message, log.context, log.details);
        break;
      case LogLevel.ERROR:
      case LogLevel.FATAL:
        console.error(message, log.context, log.details, log.stack);
        break;
    }
  }

  /**
   * 获取日志级别
   */
  private getLogLevel(error: AppError): LogLevel {
    switch (error.code) {
      case 'VALIDATION_ERROR':
        return LogLevel.WARN;
      case 'AUTH_ERROR':
        return LogLevel.WARN;
      case 'NETWORK_ERROR':
        return LogLevel.ERROR;
      case 'STORAGE_ERROR':
        return LogLevel.ERROR;
      default:
        return LogLevel.ERROR;
    }
  }

  /**
   * 生成日志 ID
   */
  private generateLogId(): string {
    return `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 获取用户友好的错误消息
   */
  getUserMessage(error: AppError): string {
    const userMessages: Record<string, string> = {
      STORAGE_ERROR: '数据保存失败，请检查网络连接后重试',
      AUTH_ERROR: '登录已过期，请重新登录',
      NETWORK_ERROR: '网络连接失败，请检查网络后重试',
      VALIDATION_ERROR: error.message || '输入数据有误，请检查后重试',
      BUSINESS_ERROR: error.message || '操作失败，请重试',
      UNKNOWN_ERROR: '系统繁忙，请稍后重试',
    };

    return userMessages[error.code] || userMessages.UNKNOWN_ERROR;
  }

  /**
   * 获取所有错误日志
   */
  getLogs(): ErrorLog[] {
    return [...this.logs];
  }

  /**
   * 清除错误日志
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * 导出错误日志
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * 捕获异步操作错误
   */
  static async capture<T>(
    operation: () => Promise<T>,
    context?: any
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      throw errorHandler.handleError(error, context);
    }
  }

  /**
   * 捕获同步操作错误
   */
  static captureSync<T>(
    operation: () => T,
    context?: any
  ): T {
    try {
      return operation();
    } catch (error) {
      throw errorHandler.handleError(error, context);
    }
  }

  /**
   * 包装异步函数返回错误安全的 Promise
   */
  static async safe<T>(
    operation: () => Promise<T>,
    context?: any
  ): Promise<[error: AppError | null, data: T | null]> {
    try {
      const data = await operation();
      return [null, data];
    } catch (error) {
      const appError = errorHandler.handleError(error, context);
      return [appError, null];
    }
  }
}

// =============================================
// 单例导出
// =============================================

export const errorHandler = new ErrorHandler();

// =============================================
// 工具函数
// =============================================

/**
 * 判断是否为网络错误
 */
export function isNetworkError(error: Error): boolean {
  return error instanceof NetworkError;
}

/**
 * 判断是否为存储错误
 */
export function isStorageError(error: Error): boolean {
  return error instanceof StorageError;
}

/**
 * 判断是否为认证错误
 */
export function isAuthError(error: Error): boolean {
  return error instanceof AuthError;
}

/**
 * 判断是否为验证错误
 */
export function isValidationError(error: Error): boolean {
  return error instanceof ValidationError;
}

/**
 * 格式化错误信息供前端显示
 */
export function formatErrorForDisplay(error: Error | AppError | unknown): {
  message: string;
  code: string;
  isDev: boolean;
  details?: any;
} {
  const appError = error instanceof AppError
    ? error
    : error instanceof Error
    ? new AppError(error.message, 'UNKNOWN_ERROR', 500, error.stack)
    : new AppError(String(error), 'UNKNOWN_ERROR', 500);

  return {
    message: errorHandler.getUserMessage(appError),
    code: appError.code,
    isDev: errorHandler['isDevMode'],
    details: errorHandler['isDevMode'] ? appError.details : undefined,
  };
}
